/**
 * readStoredAuth — read the persisted `token` + `user` pair from
 * localStorage TOGETHER, as a single validated unit (bug-hunt F1 / PR #441
 * review).
 *
 * WHY THIS EXISTS
 * ----------------
 * App.js's root component used to seed `token` and `user` from two
 * SEPARATE `useState` initializers. A corrupt 'user' entry (e.g. a torn
 * write from an interrupted tab close) throws out of `JSON.parse` —
 * caught, and on catch we clear BOTH the 'user' and 'token' localStorage
 * keys (a corrupt user usually means a torn write, so leaving the token
 * behind risks an authenticated shell with no user object). But `token`'s
 * initializer ran BEFORE `user`'s (React calls hook initializers in
 * declaration order, and `token` was declared first) — so `token`'s
 * IN-MEMORY state had already been captured from localStorage by the time
 * `user`'s cleanup ran. The result: a corrupt-user boot cleared
 * localStorage correctly but left a stale, orphaned token alive in React
 * state (harmless only because nothing besides localStorage itself ever
 * fed that in-memory `token` to a real request — the axios Authorization
 * header comes from a request interceptor, setupAxiosAuthHeader.js, that
 * reads `localStorage.getItem('token')` fresh on every call rather than
 * from any component's React state; a structural invariant gap all the
 * same).
 *
 * Reading both keys together, through one function, makes that divergence
 * impossible: a corrupt user ALWAYS yields {token: null, user: null}, in
 * memory as well as in storage. Mirrors the extraction pattern used for the
 * other pure-ish utils in this directory (tokenFromUrl.js, etc.) — usable
 * from App.js's `useState` initializers and unit-testable without
 * rendering React at all.
 *
 * @returns {{token: string|null, user: object|null}}
 */
export function readStoredAuth() {
  const storedToken = localStorage.getItem('token');
  const storedUser = localStorage.getItem('user');
  if (!storedUser) {
    // No user on file — a bare token with no user is a pre-existing,
    // legitimate (if unusual) state, NOT the corrupt-write case this
    // function guards against, so it is left untouched.
    return { token: storedToken, user: null };
  }
  try {
    return { token: storedToken, user: JSON.parse(storedUser) };
  } catch {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    return { token: null, user: null };
  }
}

export default readStoredAuth;
